import {
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import {
  EmployeeService,
  RoomService,
  ServiceCategoryService,
  ServiceService,
  UserOfferService,
  AvailableOfferService,
  ServicePackageService,
  AvailablePackageService,
} from '@beautyapp/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTagComponent } from 'ng-zorro-antd/tag';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzPopoverDirective } from 'ng-zorro-antd/popover';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { InputModule } from '@beautyapp/form';
import { NzFormModule } from 'ng-zorro-antd/form';
import { catchError, defaultIfEmpty, debounceTime, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { SelectComponent } from '@beautyapp/form';
import { NzModalService } from 'ng-zorro-antd/modal';
import {
  PackageSellDialogComponent,
  PackageSellDialogData,
  PackageSellDialogResult,
} from './package-sell-dialog/package-sell-dialog.component';

@Component({
  selector: 'beautyapp-choose-service',
  standalone: true,
  templateUrl: './choose-service.component.html',
  imports: [
    CommonModule,
    NzTableModule,
    NzTabsModule,
    TranslateModule,
    NzSpaceModule,
    NzTagComponent,
    NzButtonComponent,
    NzInputModule,
    NzIconModule,
    ReactiveFormsModule,
    FormsModule,
    InputModule,
    SelectComponent,
    NzFormModule,
    NzPopoverDirective,
  ],
})
export class ChooseServiceComponent {
  private serviceService = inject(ServiceService);
  private serviceCategoryService = inject(ServiceCategoryService);
  private employeeService = inject(EmployeeService);
  private roomService = inject(RoomService);
  private userOfferService = inject(UserOfferService);
  private packageService = inject(ServicePackageService);
  private translate = inject(TranslateService);
  private modalService = inject(NzModalService);

  // ============ INPUTS ============

  /** Customer user ID for querying available offer services */
  userId = input<string | null>(null);

  /** Filter by employee ID */
  employeeId = input<string | null>(null);

  /** Filter by room ID */
  roomId = input<string | null>(null);

  /**
   * Booking date the operator is creating the visit for. When provided, the
   * package dropdown asks the BE for packages valid on THAT day (not today),
   * so an operator booking a future visit doesn't see packages that would
   * expire before then (#2655).
   */
  bookingDate = input<Date | string | null>(null);

  // ============ OUTPUTS ============

  serviceSelected = output<any>();
  offerServiceSelected = output<AvailableOfferService>();
  packageServiceSelected = output<AvailablePackageService>();

  // ============ STATE ============

  /** Active tab index: 0 = services, 1 = offers */
  activeTabIndex = signal(0);

  // No default (beaut-606, reverted on request): the list opens on every
  // service, and the operator narrows it by employee if they want to.
  selectedEmployeeId = signal<string | null>(null);
  selectedRoomId = linkedSignal(() => this.roomId());

  searchForm = new FormGroup({
    search: new FormControl<string | null>(null),
    serviceCategoryId: new FormControl<string | null>(null),
    serviceType: new FormControl<'STANDARD' | 'MINUTE'>('STANDARD'),
  });

  page = signal(1);
  pageSize = signal(10);

  serviceTypes = [
    {
      label: this.translate.instant('services.form.standard'),
      value: 'STANDARD',
    },
    { label: this.translate.instant('services.form.minute'), value: 'MINUTE' },
  ];

  // ============ RESOURCES ============

  categoriesResource = rxResource({
    stream: () =>
      this.serviceCategoryService.dropdown().pipe(
        defaultIfEmpty([]),
        catchError(() => of([]))
      ),
  });

  employeesResource = rxResource({
    stream: () =>
      this.employeeService.getDropdown().pipe(
        map((employees: any[]) =>
          employees.map((e) => ({
            ...e,
            fullName: e.user?.fullName || '',
          }))
        ),
        defaultIfEmpty([]),
        catchError(() => of([]))
      ),
  });

  roomsResource = rxResource({
    stream: () =>
      this.roomService.getAll().pipe(
        defaultIfEmpty([]),
        catchError(() => of([]))
      ),
  });

  searchValues = toSignal(
    this.searchForm.valueChanges.pipe(debounceTime(300)),
    { initialValue: this.searchForm.value }
  );

  serviceResources = rxResource({
    params: () => ({
      page: this.page(),
      pageSize: this.pageSize(),
      search: this.searchValues()?.search || undefined,
      serviceCategoryId: this.searchValues()?.serviceCategoryId || undefined,
      serviceType: this.searchValues()?.serviceType || undefined,
      employeeId: this.selectedEmployeeId() || undefined,
      roomId: this.selectedRoomId() || undefined,
    }),
    stream: ({ params }) =>
      this.serviceService
        .getFilteredServices({
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
          serviceCategoryId: params.serviceCategoryId,
          serviceType: params.serviceType,
          employeeId: params.employeeId,
          roomId: params.roomId,
        })
        .pipe(
          defaultIfEmpty({ data: [], meta: { itemCount: 0 } }),
          catchError(() => of({ data: [], meta: { itemCount: 0 } }))
        ),
  });

  /** Available offer services resource */
  offerServicesResource = rxResource({
    params: () => ({ userId: this.userId() }),
    stream: ({ params }) =>
      this.userOfferService
        .getAvailableServices(params.userId || undefined)
        .pipe(
          map((res) => res.services),
          defaultIfEmpty([]),
          catchError(() => of([]))
        ),
  });

  /** Available package services resource */
  packageServicesResource = rxResource({
    params: () => ({
      userId: this.userId(),
      bookingDate: this.bookingDate(),
    }),
    stream: ({ params }) => {
      if (!params.userId) return of([]);
      return this.packageService
        .getUserBalance(
          params.userId,
          undefined,
          params.bookingDate ?? undefined
        )
        .pipe(
          map((res) => res.services),
          defaultIfEmpty([]),
          catchError(() => of([]))
        );
    },
  });

  // ============ COMPUTED ============

  categories = computed(() => this.categoriesResource.value() || []);
  employees = computed(() => this.employeesResource.value() || []);
  rooms = computed(() => this.roomsResource.value() || []);
  loading = computed(() => this.serviceResources.isLoading());
  total = computed(() => this.serviceResources.value()?.meta?.itemCount || 0);
  services = computed(() => this.serviceResources.value()?.data || []);
  offerServices = computed(() => this.offerServicesResource.value() || []);
  offerServicesLoading = computed(() => this.offerServicesResource.isLoading());
  packageServices = computed(() => this.packageServicesResource.value() || []);
  packageServicesLoading = computed(() =>
    this.packageServicesResource.isLoading()
  );

  // ============ METHODS ============

  paginatorChange($event: NzTableQueryParams) {
    this.page.set($event.pageIndex);
    this.pageSize.set($event.pageSize);
  }

  getEmployeePrice(service: any): number | null {
    const empId = this.employeeId();
    if (!empId || !service.employeeServices?.length) return null;
    const es = service.employeeServices.find(
      (e: any) => e.employeeId === empId
    );
    return es?.price != null ? +es.price : null;
  }

  clearFilters() {
    this.searchForm.reset({
      search: null,
      serviceCategoryId: null,
      serviceType: 'STANDARD',
    });
    this.selectedEmployeeId.set(null);
    this.selectedRoomId.set(null);
    this.page.set(1);
  }

  /**
   * Check if an offer is expiring soon (within 7 days)
   */
  isExpiringSoon(expiryDate: Date): boolean {
    const now = new Date();
    const expiry = new Date(expiryDate);
    const diffDays = Math.ceil(
      (expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );
    return diffDays <= 7;
  }

  /**
   * Open an inline sell dialog so the operator can purchase a package for
   * the current customer without leaving the booking modal. On successful
   * sale, the user's package balance is reloaded automatically so the
   * newly purchased package appears in the dropdown immediately — the
   * "auto-attach" behaviour the spec asked for.
   *
   * Requires a customer (userId) to be present on the booking form. The
   * button in the template disables itself when userId is null, but we
   * guard here too in case this method is invoked programmatically.
   */
  openPackagePurchase(): void {
    const uid = this.userId();
    if (!uid) return;

    const ref = this.modalService.create<
      PackageSellDialogComponent,
      PackageSellDialogData,
      PackageSellDialogResult
    >({
      nzTitle: 'პაკეტის შეძენა',
      nzContent: PackageSellDialogComponent,
      nzData: { userId: uid },
      nzWidth: 900,
      nzFooter: null,
      nzMaskClosable: false,
    });

    ref.afterClose.subscribe((result) => {
      if (result?.purchaseId) {
        // Auto-refresh the customer's package balance so the new purchase
        // appears in the list without an extra click. Switch to the
        // Packages tab so the operator sees the result immediately.
        this.reloadPackageServices();
        this.activeTabIndex.set(1);
      }
    });
  }

  /**
   * Re-fetch the user's package balance. Called after a successful
   * inline package purchase, and exposed for the optional "Refresh"
   * button in the Packages tab.
   */
  reloadPackageServices(): void {
    this.packageServicesResource.reload();
  }
}
